import { changed, rejected, type Command, type CommandExecution } from './commands';
import { damage, type DamageSet } from './damage';
import { intersectRect, transformPoint, type Point, type Rect } from './geometry';
import { createId, findLayer, layerDocumentTransform, type EditorDocument, type LayerBase } from './model';
import { canonicalizeRegions, pointIsSelected, type SelectionState } from './selection';
import { RasterTileStore } from './tiles';

export interface RefineSelectionParams {
  shift: number;
  smooth: number;
  feather: number;
  contrast: number;
}

export function refineSelectionRegions(selection: SelectionState, params: Pick<RefineSelectionParams, 'shift' | 'smooth'>, bounds: Rect): SelectionState {
  if (selection.regions.length === 0) return { regions: [], inverted: selection.inverted };
  const signedShift = selection.inverted ? -params.shift : params.shift;
  const smoothing = Math.max(0, Math.min(20, params.smooth)) / 2;
  const grow = signedShift + smoothing;
  const expanded = canonicalizeRegions(selection.regions.flatMap((region) => {
    const adjusted = {
      x: region.x - grow,
      y: region.y - grow,
      width: region.width + grow * 2,
      height: region.height + grow * 2,
    };
    if (adjusted.width <= 0 || adjusted.height <= 0) return [];
    const clipped = intersectRect(adjusted, bounds);
    return clipped ? [clipped] : [];
  }));
  const regions = smoothing <= 0 ? expanded : canonicalizeRegions(expanded.flatMap((region) => {
    const adjusted = {
      x: region.x + smoothing,
      y: region.y + smoothing,
      width: region.width - smoothing * 2,
      height: region.height - smoothing * 2,
    };
    return adjusted.width > 0 && adjusted.height > 0 ? [adjusted] : [];
  }));
  return { regions, inverted: selection.inverted };
}

function signedDistanceToRect(point: Point, rect: Rect): number {
  const dx = Math.max(rect.x - point.x, 0, point.x - (rect.x + rect.width));
  const dy = Math.max(rect.y - point.y, 0, point.y - (rect.y + rect.height));
  if (dx > 0 || dy > 0) return -Math.hypot(dx, dy);
  return Math.min(point.x - rect.x, rect.x + rect.width - point.x, point.y - rect.y, rect.y + rect.height - point.y);
}

export function refinedSelectionCoverage(selection: SelectionState, point: Point, feather: number, contrast: number): number {
  const radius = Math.max(0, Math.min(50, feather));
  let coverage: number;
  if (radius <= 0) coverage = pointIsSelected(selection, point) ? 1 : 0;
  else if (selection.regions.length === 0) coverage = 1;
  else {
    const distance = Math.max(...selection.regions.map((region) => signedDistanceToRect(point, region)));
    const selected = Math.max(0, Math.min(1, 0.5 + distance / (radius * 2)));
    coverage = selection.inverted ? 1 - selected : selected;
  }
  const strength = 1 + Math.max(0, Math.min(100, contrast)) / 20;
  return Math.max(0, Math.min(1, 0.5 + (coverage - 0.5) * strength));
}

export class CreateRefinedMaskCommand implements Command {
  readonly label = 'Output refined selection to mask';
  private previous?: LayerBase['mask'];
  private created?: NonNullable<LayerBase['mask']>;

  constructor(
    private readonly layerId: string,
    private readonly selection: SelectionState,
    private readonly feather: number,
    private readonly contrast: number,
  ) {}

  retainedBytes(): number {
    return 256 + (this.previous?.tiles.byteLength() ?? 0) + (this.created?.tiles.byteLength() ?? 0);
  }

  execute(document: EditorDocument): CommandExecution {
    const layer = findLayer(document.root, this.layerId);
    if (!layer || layer.kind !== 'raster') return rejected('Refined masks require an active raster layer.');
    if (layer.raster.width * layer.raster.height > 16_000_000) return rejected('Refined mask output exceeds the 16 megapixel safety limit.');
    if (this.created) {
      layer.mask = this.created;
      return changed(this.damage());
    }
    const effective = layerDocumentTransform(document.root, layer.id) ?? layer.transform;
    const defaultAlpha = this.selection.inverted || this.selection.regions.length === 0 ? 255 : 0;
    const tiles = new RasterTileStore(layer.raster.width, layer.raster.height);
    const columns = Math.ceil(tiles.width / tiles.tileSize);
    const rows = Math.ceil(tiles.height / tiles.tileSize);
    for (let tileY = 0; tileY < rows; tileY += 1) for (let tileX = 0; tileX < columns; tileX += 1) {
      const dimensions = tiles.tileDimensions(tileX, tileY);
      const pixels = new Uint8ClampedArray(dimensions.width * dimensions.height * 4);
      let differs = false;
      for (let y = 0; y < dimensions.height; y += 1) for (let x = 0; x < dimensions.width; x += 1) {
        const local = { x: tileX * tiles.tileSize + x + 0.5, y: tileY * tiles.tileSize + y + 0.5 };
        const alpha = Math.round(refinedSelectionCoverage(this.selection, transformPoint(effective, local), this.feather, this.contrast) * 255);
        const offset = (y * dimensions.width + x) * 4;
        pixels[offset] = 255;
        pixels[offset + 1] = 255;
        pixels[offset + 2] = 255;
        pixels[offset + 3] = alpha;
        if (alpha !== defaultAlpha) differs = true;
      }
      if (differs) tiles.setTile(tileX, tileY, pixels);
    }
    this.previous = layer.mask;
    this.created = { id: createId('mask'), enabled: true, linked: true, defaultAlpha, tiles };
    layer.mask = this.created;
    return changed(this.damage());
  }

  undo(document: EditorDocument): void {
    const layer = findLayer(document.root, this.layerId);
    if (!layer) return;
    if (this.previous) layer.mask = this.previous;
    else delete layer.mask;
  }

  damage(): DamageSet {
    return damage({ layerIds: [this.layerId] });
  }
}
